import { useState } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import FormattedDateTime from 'I18n/components/FormattedDateTime';
import { Button } from 'foundations-components/transitional/components/Button';
import { Flex } from 'foundations-components/components/Flex';
import { Dropdown } from 'foundations-components/transitional/components/Dropdown';
import { List, ListItem } from 'foundations-components/transitional/components/List';
import { Link } from 'foundations-components/transitional/components/Link';
import { useMediaQuery } from 'academy-common-lib/utils/Styling';
import { Alert } from 'foundations-components/transitional/components/Alert';
import { LoadingButton } from 'foundations-components/transitional/components/LoadingButton';
import { FloatingAlertStore } from 'foundations-components/transitional/components/FloatingAlertList';
import { ACRouterButton } from '../../components/ACRouterButton';
import UsageTracker from '../../utils/UsageTracker';
import useSearchParams from '../../hooks/useSearchParams';
import { HasGate } from '../accounts/components/HasGate';
import { useRegisterForSession } from './liveEventQueries';
import { ZoomRegistrationFallbackModal } from './ZoomRegistrationFallbackModal';
import { getSessionStartedAt, getSessionStartTime } from './sessionTimeUtils';
import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
function SessionAlert({
  body,
  cta,
  title,
  type
}) {
  const {
    isMobile
  } = useMediaQuery();
  return /*#__PURE__*/_jsx(Alert, {
    "data-test-id": "live-event-registration-alert",
    marginBlock: "800",
    titleText: title,
    type: type,
    children: /*#__PURE__*/_jsxs(Flex, {
      alignItems: isMobile ? 'stretch' : 'center',
      flexDirection: isMobile ? 'column' : 'row',
      flexGrow: 1,
      gap: "400",
      inlineSize: "100%",
      justifyContent: "space-between",
      children: [body, cta]
    })
  });
}
const LiveSessionAlertBody = ({
  liveSession,
  isStartingSoon,
  isRegistered
}) => {
  const startTime = /*#__PURE__*/_jsx(FormattedDateTime, {
    format: "LT",
    type: "userTz",
    value: isStartingSoon ? getSessionStartTime(liveSession) : getSessionStartedAt(liveSession)
  });
  if (isStartingSoon) {
    return isRegistered ? /*#__PURE__*/_jsx(FormattedReactMessage, {
      message: "academyComponents.liveEventOverviewModal.alert.startingSoonRegisteredBody",
      options: {
        startTime
      }
    }) : /*#__PURE__*/_jsx(FormattedReactMessage, {
      message: "academyComponents.liveEventOverviewModal.alert.startingSoonUnregisteredBody",
      options: {
        startTime
      }
    });
  }
  return isRegistered ? /*#__PURE__*/_jsx(FormattedReactMessage, {
    message: "academyComponents.liveEventOverviewModal.alert.liveRegisteredBody",
    options: {
      startTime
    }
  }) : /*#__PURE__*/_jsx(FormattedReactMessage, {
    message: "academyComponents.liveEventOverviewModal.alert.liveUnregisteredBody",
    options: {
      startTime
    }
  });
};
function RegisterLiveSessionButton({
  contentName,
  liveEventId,
  liveSession,
  onRegistered
}) {
  const [fallbackUrl, setFallbackUrl] = useState();
  const [{
    overviewSource
  }] = useSearchParams();
  const {
    registerForSession,
    registrationLoading
  } = useRegisterForSession({
    eventId: liveEventId,
    onCompleted: onRegistered ? data => onRegistered(liveSession, data) : undefined,
    onFallback: setFallbackUrl,
    onError: () => {
      FloatingAlertStore.addAlert({
        type: 'danger',
        message: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "academyComponents.liveEventOverviewModal.registrationError"
        })
      });
    }
  });
  const handleRegister = () => {
    UsageTracker.trackStandalone('Content Interaction', Object.assign({
      action: 'live-event-register-click',
      contentType: 'LIVE_EVENT',
      contentId: liveEventId,
      contentName,
      sessionId: liveSession.id,
      startTime: getSessionStartTime(liveSession)
    }, overviewSource && {
      source: overviewSource
    }));
    void registerForSession(liveSession.eventSessionExternalId);
  };
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [fallbackUrl && /*#__PURE__*/_jsx(ZoomRegistrationFallbackModal, {
      fallbackUrl: fallbackUrl,
      onClose: () => setFallbackUrl(undefined)
    }), /*#__PURE__*/_jsx(LoadingButton, {
      loading: registrationLoading,
      onClick: handleRegister,
      use: "primary",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "academyComponents.liveEventOverviewModal.registerForWorkshop"
      })
    })]
  });
}
function WatchRecordingCta({
  contentName,
  liveEventId,
  sessionId,
  startTime,
  recordingUrls
}) {
  const trackClick = () => {
    UsageTracker.trackStandalone('Content Interaction', {
      action: 'live-event-watch-recording-click',
      contentType: 'LIVE_EVENT',
      contentId: liveEventId,
      contentName,
      sessionId,
      startTime
    });
  };
  if (recordingUrls.length === 1) {
    return /*#__PURE__*/_jsx(Button, {
      external: true,
      href: recordingUrls[0],
      onClick: trackClick,
      target: "_blank",
      use: "secondary",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "academyComponents.liveEventOverviewModal.watchRecording"
      })
    });
  }
  return /*#__PURE__*/_jsx(Dropdown, {
    buttonText: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "academyComponents.liveEventOverviewModal.watchRecordings"
    }),
    buttonUse: "secondary",
    closeOnMenuClick: true,
    children: /*#__PURE__*/_jsx(List, {
      children: recordingUrls.map((url, index) => /*#__PURE__*/_jsx(ListItem, {
        children: /*#__PURE__*/_jsx(Link, {
          external: true,
          href: url,
          onClick: trackClick,
          target: "_blank",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "academyComponents.liveEventOverviewModal.recordingPart",
            options: {
              number: index + 1
            }
          })
        })
      }, url))
    })
  });
}
const LiveEventRegistrationAlert = ({
  contentName,
  hasRegisteredUpcoming,
  isLiveSessionRegistered,
  isStartingSoon = false,
  joinUrl,
  liveEventId,
  liveSession,
  onRegistered,
  pastRecording,
  viewRegisteredEventsTo
}) => {
  const liveTitle = isStartingSoon ? /*#__PURE__*/_jsx(FormattedMessage, {
    message: "academyComponents.liveEventOverviewModal.alert.startingSoonTitle"
  }) : /*#__PURE__*/_jsx(FormattedMessage, {
    message: "academyComponents.liveEventOverviewModal.alert.liveNowTitle"
  });
  if (liveSession && isLiveSessionRegistered) {
    return /*#__PURE__*/_jsx(SessionAlert, {
      body: /*#__PURE__*/_jsx(LiveSessionAlertBody, {
        isRegistered: true,
        isStartingSoon: isStartingSoon,
        liveSession: liveSession
      }),
      cta: joinUrl ? /*#__PURE__*/_jsx(Button, {
        external: true,
        href: joinUrl,
        onClick: () => {
          UsageTracker.trackStandalone('Content Interaction', {
            action: 'live-event-join-session-click',
            contentType: 'LIVE_EVENT',
            contentId: liveEventId,
            contentName,
            sessionId: liveSession.id,
            startTime: getSessionStartTime(liveSession)
          });
        },
        target: "_blank",
        use: "primary",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "academyComponents.liveEventOverviewModal.joinSession"
        })
      }) : undefined,
      title: liveTitle,
      type: "success"
    });
  }
  if (liveSession) {
    return /*#__PURE__*/_jsx(SessionAlert, {
      body: /*#__PURE__*/_jsx(LiveSessionAlertBody, {
        isRegistered: false,
        isStartingSoon: isStartingSoon,
        liveSession: liveSession
      }),
      cta: /*#__PURE__*/_jsx(RegisterLiveSessionButton, {
        contentName: contentName,
        liveEventId: liveEventId,
        liveSession: liveSession,
        onRegistered: onRegistered
      }),
      title: liveTitle,
      type: "success"
    });
  }
  if (hasRegisteredUpcoming) {
    return /*#__PURE__*/_jsx(SessionAlert, {
      body: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "academyComponents.liveEventOverviewModal.alert.upcomingRegisteredBody"
      }),
      cta: /*#__PURE__*/_jsx(HasGate, {
        gate: "AcademyAchievements::MyLearningSearchLiveEventRollout",
        children: /*#__PURE__*/_jsx(ACRouterButton, {
          to: viewRegisteredEventsTo,
          use: "secondary",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "academyComponents.liveEventOverviewModal.viewRegisteredEvents"
          })
        })
      }),
      title: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "academyComponents.liveEventOverviewModal.alert.registeredTitle"
      }),
      type: "info"
    });
  }
  if (pastRecording) {
    const body = pastRecording.attended ? /*#__PURE__*/_jsx(FormattedMessage, {
      message: "academyComponents.liveEventOverviewModal.alert.pastAttendedBody"
    }) : /*#__PURE__*/_jsx(FormattedMessage, {
      message: "academyComponents.liveEventOverviewModal.alert.pastRegisteredBody"
    });
    return /*#__PURE__*/_jsx(SessionAlert, {
      body: body,
      cta: /*#__PURE__*/_jsx(WatchRecordingCta, {
        contentName: contentName,
        liveEventId: liveEventId,
        recordingUrls: pastRecording.recordingUrls,
        sessionId: pastRecording.sessionId,
        startTime: pastRecording.startTime
      }),
      title: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "academyComponents.liveEventOverviewModal.alert.watchRecordingTitle"
      }),
      type: "tip"
    });
  }
  return null;
};
export { LiveEventRegistrationAlert };